import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, InputNumber, Modal, Segmented, Select, Switch, message } from 'antd';
import dayjs from 'dayjs';
import api from '../../../lib/api';
import type { Project } from '../../../types/project';
import type { PlanTask } from '../../../types/plan';

type TaskEditModalProps = {
  open: boolean;
  task: PlanTask | null;
  projects: Project[];
  categories: string[];
  onCancel: () => void;
  onSaved: () => void;
};

type FormValues = {
  title: string;
  project_id: number | null;
  category: string | null;
  planned_for: dayjs.Dayjs | null;
  important: boolean;
  // 'none' 是表单里「未标」的取值。提交时转回 null —— 与 work_type 同一个写法。
  priority: 'p1' | 'p2' | 'p3' | 'p4' | 'none';
  work_type: 'deep' | 'simple' | 'shallow' | 'none';
  estimated_minutes: number | null;
};

const TaskEditModal: React.FC<TaskEditModalProps> = ({
  open,
  task,
  projects,
  categories,
  onCancel,
  onSaved,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  // `projects` 只是 .active 的项目（见 Task 5 的 PlanController#show）——新建
  // 任务/挪任务不该挪进已归档项目。但如果正在编辑的这个任务当前就挂在一个已
  // 归档项目上（决策 3：这类任务在「项目」分组视图里照常出组，不会消失），这里
  // 就必须把那一个项目也补进选项列表，否则 Select 找不到 value 对应的
  // option，会显示成空白——用户什么都没做就点保存，会把 project_id 悄悄清空。
  // `task.project` 是 TaskPresenter 吐出来的当前归属快照（{id, name, color}），
  // 不在 `projects`（.active 列表）里出现 = 一定是已归档（项目被删除时
  // Task#project_id 会被置空，task.project 会是 null，不会走到这个分支）。
  const projectOptions = useMemo(() => {
    const options = projects.map((project) => ({ value: project.id, label: project.name }));
    if (task?.project && !projects.some((project) => project.id === task.project!.id)) {
      options.push({ value: task.project.id, label: `${task.project.name}（已归档）` });
    }
    return options;
  }, [projects, task]);

  useEffect(() => {
    if (!open || !task) return;

    form.setFieldsValue({
      title: task.title,
      project_id: task.project_id,
      category: task.category,
      planned_for: task.planned_for ? dayjs(task.planned_for) : null,
      important: task.important,
      priority: task.priority ?? 'none',
      work_type: task.work_type ?? 'none',
      estimated_minutes: task.estimated_minutes,
    });
  }, [open, task, form]);

  const handleOk = async () => {
    if (!task) return;

    const values = await form.validateFields();
    setSaving(true);
    try {
      await api.plan.updateTask(task.id, {
        title: values.title,
        project_id: values.project_id ?? null,
        category: values.category ?? null,
        planned_for: values.planned_for ? values.planned_for.format('YYYY-MM-DD') : null,
        important: values.important,
        priority: values.priority === 'none' ? null : values.priority,
        work_type: values.work_type === 'none' ? null : values.work_type,
        estimated_minutes: values.estimated_minutes ?? null,
      });
      onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="编辑任务"
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="title" label="标题" rules={[{ required: true, message: '请填标题' }]}>
          <Input />
        </Form.Item>

        <Form.Item name="project_id" label="项目">
          <Select allowClear placeholder="未归项目" options={projectOptions} />
        </Form.Item>

        <Form.Item name="category" label="生活面向">
          <Select
            allowClear
            placeholder="未分类"
            options={categories.map((category) => ({ value: category, label: category }))}
          />
        </Form.Item>

        <Form.Item name="planned_for" label="计划哪天做">
          <DatePicker className="w-full" placeholder="留空 = 放回 Backlog" />
        </Form.Item>

        <div className="flex gap-6">
          <Form.Item name="important" label="重要" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>

        <Form.Item name="priority" label="优先级">
          <Segmented
            options={[
              { value: 'p1', label: 'P1' },
              { value: 'p2', label: 'P2' },
              { value: 'p3', label: 'P3' },
              { value: 'p4', label: 'P4' },
              { value: 'none', label: '未标' },
            ]}
          />
        </Form.Item>

        <Form.Item name="work_type" label="精力类型">
          <Segmented
            options={[
              { value: 'deep', label: '深度' },
              { value: 'simple', label: '简单' },
              { value: 'shallow', label: '杂事' },
              { value: 'none', label: '未标' },
            ]}
          />
        </Form.Item>

        <Form.Item name="estimated_minutes" label="预估时长（分钟）">
          <InputNumber min={1} step={15} className="w-full" placeholder="例如 120" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TaskEditModal;
